// workspaceStore — the slim selection + flags store (contract 07 §7, ORP-18634).
//
// The v3 `stores/workspaceStore.ts` was a 530-line god-store: 9 actions calling the HTTP client
// directly, 2 orchestrators, a 10-second timeout race, an auto-logout fired by a >3MB payload-size
// heuristic, a persisted hand-rolled remount counter, and a hardcoded 259-entry currency-SYMBOL
// table. All of that is GONE here:
//   - Heavy per-dataset payloads (schedules, dropdownables, compositions, preferences,
//     integrations) leave the store — they arrive via bootstrap sections and land in the
//     owning feature stores through `registerBootstrapHydrator` (contract §3, §8).
//   - The remount-counter mechanism is deleted: `session.status` (sessionStore) +
//     `<RouterView :key="workspaceId">` own the render gate / remount (§7).
//   - `currency` is exposed as an ISO-4217 **code**; symbol/placement is `useFormat`'s job (04).
//   - No HTTP-client import, no flow import — the v3 store↔flow circular reach is fixed: the store
//     is a pure state container. `useWorkspace().selectWorkspace` (composable) drives the workspace
//     switch flow, which commits the selection back here via `setSelectedWorkspace` (contract §9).
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { WorkspaceItem } from '../models/workspace.types'
import { workspacePersistStorage } from '../storage/AccountStorage'

/** A workspace attribute (settings key-event), e.g. `{ key: 'currency', value: { code: 'USD' } }`
 *  or `{ key: 'language', value: 'en' }`. Kept structural — values are polymorphic per key. */
export interface WorkspaceAttribute {
  key?: string
  value?: unknown
  [k: string]: unknown
}

export const useWorkspaceStore = defineStore(
  'workspaceStore',
  () => {
    // ── State (contract §7) ────────────────────────────────────────────
    const workspaces = ref<WorkspaceItem[]>([])
    const workspace = ref<WorkspaceItem | null>(null)
    const onlyDashboardPermission = ref(false)
    // Per-account last-selected workspace cache (persisted): accountId → workspaceId. Preserves
    // the "old-for-account" step of the workspace pick order (contract §3 parity).
    const oldWorkspaceForAccount = ref<Record<string, number | string>>({})
    // Feature flags for the current workspace, fed by the bootstrap `feature_flags` section
    // (BE-07-04, ratified R2 — NOT an HTTP action here). Not persisted: re-hydrated each boot.
    const enabledFeatures = ref<string[]>([])
    const disabledFeatures = ref<string[]>([])
    // Workspace attributes (currency, language, …). Not persisted: re-hydrated each boot.
    const attributes = ref<WorkspaceAttribute[]>([])

    // ── Getters (contract §7) ──────────────────────────────────────────
    const workspaceId = computed<WorkspaceItem['id'] | undefined>(() => workspace.value?.id)

    /** The workspace currency as an ISO-4217 **code** (e.g. `'USD'`) — never a symbol. Rendering
     *  goes through `useFormat().formatCurrency` (04). `undefined` when unset → the formatter
     *  falls back; the v3 hardcoded symbol map is deleted. */
    const currency = computed<string | undefined>(() => {
      const value = attributes.value.find((a) => a?.key === 'currency')?.value
      const code =
        value && typeof value === 'object' && 'code' in value
          ? (value as { code?: unknown }).code
          : undefined
      return typeof code === 'string' ? code : undefined
    })

    /** Flag gate for nav/route visibility (Insights, Orpheus, Workflow). Reads the bootstrap
     *  flags — on the aggregated path this is the live source, fixing the v3 bug where
     *  `checkFlag` was permanently `false` for every non-sysadmin (analysis §, BE-07-04). */
    function checkFlag(key: string): boolean {
      return enabledFeatures.value.includes(key) && !disabledFeatures.value.includes(key)
    }

    /** The attribute entry for `key` (e.g. `'currency'`, `'language'`), or `null` (v3 parity). */
    function getWorkspaceAttributesByKey(key: string): WorkspaceAttribute | null {
      return attributes.value.find((a) => a?.key === key) ?? null
    }

    // ── Actions ────────────────────────────────────────────────────────
    /** Set the account-scoped workspace list (from the mapped `account_core.workspaces`). */
    function setWorkspaces(list: WorkspaceItem[]): void {
      workspaces.value = list
    }

    /** Commit the selected workspace. Pure state write — the flow/composable owns the switch
     *  orchestration (cancel in-flight, reset scope, re-hydrate); it calls this to commit. */
    function setSelectedWorkspace(ws: WorkspaceItem | null): void {
      workspace.value = ws
    }

    /** Cache the last-selected workspace for an account (persisted old-selection). */
    function setOldWorkspaceForAccount(
      accountId: number | string,
      selectedWorkspaceId: number | string,
    ): void {
      oldWorkspaceForAccount.value = {
        ...oldWorkspaceForAccount.value,
        [accountId]: selectedWorkspaceId,
      }
    }

    /** Set the workspace attributes (mapped from the bootstrap settings section). */
    function setAttributes(next: WorkspaceAttribute[]): void {
      attributes.value = next
    }

    /** Set the workspace feature flags (mapped from the bootstrap `feature_flags` section). */
    function setFeatureFlags(enabled: string[], disabled: string[] = []): void {
      enabledFeatures.value = enabled
      disabledFeatures.value = disabled
    }

    /** The dashboard-only gate (contract §12); computed by the bootstrap finalize step. */
    function setCustomDashboardPermissions(permission: boolean): void {
      onlyDashboardPermission.value = permission
    }

    /** Reset store scope. Workspace-scoped state (selection attrs, flags, dashboard-only gate)
     *  is always cleared so a switch/boot re-hydrates fresh. With `keepList=true` the workspace
     *  list and current selection also survive a workspace switch. The per-account selection
     *  history survives both paths; a later bootstrap validates it against the fresh list. */
    function resetState(keepList = false): void {
      attributes.value = []
      enabledFeatures.value = []
      disabledFeatures.value = []
      onlyDashboardPermission.value = false
      if (!keepList) {
        workspace.value = null
        workspaces.value = []
      }
    }

    return {
      workspaces,
      workspace,
      onlyDashboardPermission,
      oldWorkspaceForAccount,
      enabledFeatures,
      disabledFeatures,
      attributes,
      workspaceId,
      currency,
      checkFlag,
      getWorkspaceAttributesByKey,
      setWorkspaces,
      setSelectedWorkspace,
      setOldWorkspaceForAccount,
      setAttributes,
      setFeatureFlags,
      setCustomDashboardPermissions,
      resetState,
    }
  },
  // secure-ls (module 01) storage; ONLY the selection subset persists (contract §7). RBAC-adjacent
  // scope (attributes, flags, onlyDashboardPermission) is re-hydrated each boot — never persisted.
  // `key` + `workspacePersistStorage`'s codec are a v3 wire contract (ORP-18641) — v3 writes this
  // same key with Base64/no-compression, so the account adapter's aes would clobber it.
  {
    persist: {
      key: 'workspaceStore',
      pick: ['oldWorkspaceForAccount', 'workspace', 'workspaces'],
      storage: workspacePersistStorage,
    },
  },
)

export type WorkspaceStore = ReturnType<typeof useWorkspaceStore>
